/**
 * Fixtures for the platform screens: the settings registry, feature flags, the audit log and jobs,
 * and admin accounts.
 *
 * Two invariants are encoded as data rather than left to a screen's judgement:
 *   - **Some settings are safety-locked.** There is no setting that turns auditing off, that
 *     disables four-eyes on write-offs, or that switches off the outcome-claim lint. A locked row
 *     says so and cannot be edited from anywhere.
 *   - **A new admin starts with zero scopes.** Least privilege is the default state, not a policy
 *     someone remembers to apply, and a zero-scope admin is a valid account, not a broken one.
 */

import type { Scope } from '../lib/rbac';

export type PlatformTone = 'error' | 'warn' | 'success' | 'info' | 'neutral';

/* ────────────────────────────  Settings registry  ──────────────────────────── */

export interface Setting {
  id: string;
  domain: string;
  key: string;
  type: 'integer' | 'decimal' | 'paise' | 'boolean' | 'string';
  value: string;
  default: string;
  /** The scope that may edit it. Reading is broader than writing throughout. */
  editable_by: Scope;
  /** Changing it is itself a four-eyes change. */
  four_eyes?: boolean;
  /** No path exists to change it — the lock is the point, not an oversight. */
  locked?: boolean;
  note: string;
}

export const SETTINGS: readonly Setting[] = [
  {
    id: 'st1',
    domain: 'Dispatch',
    key: 'dispatch.wave_size',
    type: 'integer',
    value: '6',
    default: '4',
    editable_by: 'ops_dispatch',
    note: 'Pandits notified per wave',
  },
  {
    id: 'st2',
    domain: 'Dispatch',
    key: 'dispatch.wave_expiry_minutes',
    type: 'integer',
    value: '20',
    default: '15',
    editable_by: 'ops_dispatch',
    note: 'Offer expiry before the next wave',
  },
  {
    id: 'st3',
    domain: 'Dispatch',
    key: 'dispatch.radius_km',
    type: 'decimal',
    value: '18.0',
    default: '15.0',
    editable_by: 'ops_dispatch',
    note: 'Default dispatch radius per city',
  },
  {
    id: 'st4',
    domain: 'Money',
    key: 'settlement.four_eyes_threshold_paise',
    type: 'paise',
    value: '2500000',
    default: '2500000',
    editable_by: 'finance',
    four_eyes: true,
    note: 'Releases above this need a second approver. Changing it is itself a four-eyes change.',
  },
  {
    id: 'st5',
    domain: 'Money',
    key: 'payout.completion_hold_hours',
    type: 'integer',
    value: '24',
    default: '24',
    editable_by: 'finance',
    note: 'Hold after OTP completion',
  },
  {
    id: 'st6',
    domain: 'Money',
    key: 'refunds.max_goodwill_paise_without_second_approval',
    type: 'paise',
    value: '50000',
    default: '50000',
    editable_by: 'finance',
    four_eyes: true,
    note: 'Goodwill above this parks in the four-eyes queue',
  },
  {
    id: 'st13',
    domain: 'Money',
    key: 'checkout.slot_hold_minutes',
    type: 'integer',
    value: '15',
    default: '15',
    editable_by: 'finance',
    note: 'The one legitimate countdown in the product',
  },
  {
    id: 'st7',
    domain: 'Supply',
    key: 'kyc.badge_expiry_months.identity',
    type: 'integer',
    value: '36',
    default: '36',
    editable_by: 'supply_kyc',
    note: 'Identity badge validity',
  },
  {
    id: 'st8',
    domain: 'Supply',
    key: 'quality.cancellation_suspend_pct',
    type: 'decimal',
    value: '8.0',
    default: '10.0',
    editable_by: 'supply_kyc',
    note: 'Suspension review trigger',
  },
  {
    id: 'st9',
    domain: 'Content',
    key: 'seo.page_ceiling',
    type: 'integer',
    /* Matches the CMS screen's budget exactly — one number, two surfaces. */
    value: '13200',
    default: '13200',
    editable_by: 'catalogue_content',
    note: 'Hard ceiling — a breach fails the deploy',
  },
  {
    id: 'st10',
    domain: 'Content',
    key: 'content.outcome_claim_lint',
    type: 'boolean',
    value: 'true',
    default: 'true',
    editable_by: 'superadmin',
    four_eyes: true,
    locked: true,
    note: 'Blocks publishing outcome claims. Safety-locked — there is no setting that turns auditing or this lint off.',
  },
  {
    id: 'st11',
    domain: 'Platform',
    key: 'app.min_supported_build',
    type: 'string',
    value: '2.14.0',
    default: '2.10.0',
    editable_by: 'superadmin',
    note: 'Older builds are force-upgraded',
  },
  {
    id: 'st12',
    domain: 'Platform',
    key: 'feature.pandit_mode_earnings_v2',
    type: 'boolean',
    value: 'false',
    default: 'false',
    editable_by: 'superadmin',
    note: 'Rollout flag',
  },
  {
    id: 'st14',
    domain: 'People',
    key: 'profile.closure_grace_days',
    type: 'integer',
    value: '30',
    default: '30',
    editable_by: 'support',
    four_eyes: true,
    locked: true,
    note: 'DPDP closure grace. A login by the devotee cancels the closure. Statutory floor — cannot be shortened.',
  },
  {
    id: 'st15',
    domain: 'People',
    key: 'profile.family_members_max',
    type: 'integer',
    value: '25',
    default: '25',
    editable_by: 'support',
    note: 'Family records per account',
  },
  {
    id: 'st16',
    domain: 'People',
    key: 'profile.addresses_max',
    type: 'integer',
    value: '10',
    default: '10',
    editable_by: 'support',
    note: 'Saved addresses per account',
  },
];

export interface Revision {
  id: string;
  when: string;
  who: string;
  key: string;
  from: string;
  to: string;
  why: string;
}

export const REVISIONS: readonly Revision[] = [
  {
    id: 'r1',
    when: 'Today, 2:14 PM',
    who: 'ops/priya',
    key: 'dispatch.wave_size',
    from: '4',
    to: '6',
    why: 'Gaya coverage below 50%',
  },
  {
    id: 'r2',
    when: '9 May, 11:02 AM',
    who: 'finance/kavita',
    key: 'settlement.four_eyes_threshold_paise',
    from: '5000000',
    to: '2500000',
    why: 'Tightened back to the platform default after the May festival week',
  },
  {
    id: 'r3',
    when: '7 May, 6:40 PM',
    who: 'supply/anand',
    key: 'quality.cancellation_suspend_pct',
    from: '10.0',
    to: '8.0',
    why: 'Tightened after 3 no-shows',
  },
];

export const SETTING_DOMAINS = ['Dispatch', 'Money', 'Supply', 'People', 'Content', 'Platform'] as const;

/* ────────────────────────────  Audit and jobs  ──────────────────────────── */

export interface AuditEvent {
  id: string;
  at: string;
  actor: string;
  category: string;
  tone: PlatformTone;
  action: string;
  entity: string;
  why: string;
  scope_used: string;
}

/** Sensitive values are redacted in the record itself: the log proves the act, it does not leak. */
export const DIGESTS: readonly { id: string; month: string; events: string; digest: string; status: string; tone: PlatformTone }[] = [
  { id: 'd1', month: 'Apr 2026', events: '12,004', digest: '4c81…7ae2', status: 'Sealed', tone: 'success' },
  { id: 'd2', month: 'Mar 2026', events: '9,881', digest: 'b019…33f7', status: 'Sealed', tone: 'success' },
  { id: 'd3', month: 'May 2026', events: '13,442', digest: '—', status: 'Open', tone: 'info' },
];


/* ────────────────────────────  Admin accounts  ────────────────────────────
 *
 * The roster used to live here. It is `GET /v1/admin/admins` now, and the fixture was deleted
 * rather than left beside the live call — a fixture that outlives the thing it stood in for is the
 * one that eventually gets rendered by mistake.
 */

export const MATRIX_COLS = ['ops_dispatch', 'supply_kyc', 'finance', 'catalogue_content', 'support', 'growth'] as const;

/** R read · W create and update · D destructive. A feature screen may narrow a cell, never widen it. */
export const RBAC_MATRIX: readonly (readonly string[])[] = [
  ['dispatch_admin', 'RWD', '—', '—', '—', 'R', '—'],
  ['kyc_admin', '—', 'RWD', '—', '—', 'R', '—'],
  ['catalogue_admin', 'R', '—', 'R', 'RWD', 'R', 'R'],
  ['geo_admin', 'R', 'R', '—', 'RWD', '—', '—'],
  ['temples_admin', 'R', 'R', '—', 'RWD', '—', '—'],
  ['payments_admin', 'R', '—', 'RW', '—', 'R', '—'],
  ['ledger_admin', '—', '—', 'RW', '—', '—', '—'],
  ['payouts_admin', '—', '—', 'RWD', '—', '—', '—'],
  ['refunds_admin', 'R', '—', 'RWD', '—', 'RW', '—'],
  ['disputes_admin', 'R', '—', 'R', '—', 'RW', '—'],
  ['tax_admin', '—', '—', 'RW', '—', '—', '—'],
  ['reviews_admin', 'R', 'R', '—', '—', 'RWD', 'R'],
  ['support_admin', 'R', '—', '—', '—', 'RWD', '—'],
  ['users_admin', 'R', '—', '—', '—', 'RWD', 'R'],
  ['cms_admin', '—', '—', '—', 'RWD', '—', 'RW'],
  ['campaigns_admin', '—', '—', '—', '—', '—', 'RWD'],
  ['settings_admin', 'R', 'R', 'RW', 'RW', 'R', 'RW'],
  ['audit_admin', '—', '—', 'R', '—', 'R', '—'],
  ['admins_admin', '—', '—', '—', '—', '—', '—'],
];

export const SESSION_POLICY: readonly (readonly [string, string])[] = [
  ['Access token', '15 minutes, memory only'],
  ['Refresh token', '7 days, rotating and single-use'],
  ['Replay of a rotated token', 'Kills the whole session'],
  ['Absolute session TTL', '30 days'],
  ['Idle lock', '30 minutes → password + TOTP to resume'],
  ['Logout everywhere', 'Broadcast across tabs, revoked server-side'],
];

export const ACCESS_RULES: readonly (readonly [string, string])[] = [
  ['Registration', 'Invitation only — no self-registration path exists'],
  ['MFA', 'TOTP mandatory, forced at first login'],
  ['TOTP reset', 'superadmin only; never your own'],
  ['New admin', 'Zero scopes — valid, shows an empty console'],
  ['Scope change', 'Revokes sessions; claims re-mint at next login'],
  ['Last superadmin', 'Cannot be removed or deleted'],
  ['Client-side gating', 'UX only — the server enforces every rule'],
];

export const DANGER_ACTIONS: readonly { key: string; label: string; note: string }[] = [
  { key: 'totp', label: 'Reset TOTP', note: 'Forces re-enrolment at next login and revokes sessions' },
  { key: 'sessions', label: 'Revoke sessions', note: 'Signs this admin out of every browser now' },
  { key: 'deact', label: 'Deactivate account', note: 'Blocks login; the account and its audit trail remain' },
  { key: 'del', label: 'Delete account', note: 'Soft delete — type-to-confirm is their email' },
];

